import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import type { AccountType, TransactionType } from '../types';
import type { NormalizedTransactionData } from './transactionIngestion';
// ヘッダー配列を正規化（BOM/空白除去・文字列化）
function normalizeHeaderRow(headerRow: any[]): string[] {
  return headerRow.map(h => String(h ?? '').replace(/\uFEFF/g, '').trim());
}

// パース済みの行データ
export interface ParsedRowData {
  date?: string;
  amount?: number;
  category?: string;
  type?: TransactionType;
  description?: string; // 摘要
  reference_number?: string;
  rowNumber: number;
  originalData: any[];
  error?: string;
}

// 列マッピング設定
export interface ColumnMapping {
  [columnIndex: number]: '日付' | '金額' | '収支' | 'カテゴリ' | '摘要' | 'メモ' | '無視';
}

// フォーマット自動検出結果
export interface FormatDetectionResult {
  suggestedMapping: ColumnMapping;
  confidence: number; // 0-1の信頼度
  detectedPattern: string;
}

// パーサ設定
export interface FileParserConfig {
  accountType: AccountType;
  columnMapping: ColumnMapping;
  fileName: string;
  hasHeader?: boolean;
  fiscalYearStartMonth?: number; // 会計年度開始月（1-12）
}

// パース結果
export interface FileParseResult {
  parsedRows: ParsedRowData[];
  normalizedTransactions: NormalizedTransactionData[];
  errors: Array<ParsedRowData & { error: string }>;
  totalRows: number;
  validRows: number;
}

export class FileParser {
  /**
   * フォーマットを自動検出する
   */
  static detectFormat(rawData: any[][]): FormatDetectionResult {
    if (rawData.length === 0) {
      return {
        suggestedMapping: {},
        confidence: 0,
        detectedPattern: 'empty'
      };
    }

    const headerRow = rawData[0];
    const normalizedHeader = normalizeHeaderRow(headerRow);

    // シンプルな標準CSV（test_sample.csv想定）の明示的検出
    const standardHeader = ['日付', '摘要', '金額', '収支', 'メモ'];
    if (
      normalizedHeader.length >= standardHeader.length &&
      standardHeader.every((h, i) => normalizedHeader[i] === h)
    ) {
      const suggestedMapping: ColumnMapping = { 0: '日付', 1: '摘要', 2: '金額', 3: '収支', 4: 'メモ' };
      return { suggestedMapping, confidence: 1, detectedPattern: 'standard_format' };
    }

    // 年ごとのCSV（例: 年,カテゴリ,摘要,金額,収支,メモ）を明示的に検出
  const yearHeader = ['年', 'カテゴリ', '摘要', '金額', '収支'];
    if (
      normalizedHeader.length >= yearHeader.length &&
      // 先頭が「年」で、金額/収支/カテゴリの主要列を含む
      normalizedHeader[0] === '年' &&
      normalizedHeader.includes('金額') &&
      normalizedHeader.includes('収支') &&
      normalizedHeader.includes('カテゴリ')
    ) {
      const suggestedMapping: ColumnMapping = {};
      normalizedHeader.forEach((h, idx) => {
        if (h === '年') suggestedMapping[idx] = '日付';
        else if (h === '金額') suggestedMapping[idx] = '金額';
        else if (h === '収支') suggestedMapping[idx] = '収支';
        else if (h === 'カテゴリ') suggestedMapping[idx] = 'カテゴリ';
        else if (h === '摘要') suggestedMapping[idx] = '摘要';
        else if (h === 'メモ' || h === '備考') suggestedMapping[idx] = 'メモ';
        else suggestedMapping[idx] = '無視';
      });
      return { suggestedMapping, confidence: 0.95, detectedPattern: 'year_summary' };
    }

    const suggestedMapping: ColumnMapping = {};
    let confidence = 0;
    let matchCount = 0;

    // ヘッダー行を分析して列マッピングを推測
    headerRow.forEach((header: any, index: number) => {
      const headerStr = String(header).toLowerCase().trim();
      
      // 日付列の検出
      if (this.isDateColumn(headerStr)) {
        suggestedMapping[index] = '日付';
        matchCount++;
      }
      // 価格/金額列の検出
      else if (this.isPriceColumn(headerStr)) {
        suggestedMapping[index] = '金額';
        matchCount++;
      }
      // 収入列の検出
      else if (this.isIncomeColumn(headerStr)) {
        suggestedMapping[index] = '収支';
        matchCount++;
      }
      // 支出列の検出
      else if (this.isExpenseColumn(headerStr)) {
        suggestedMapping[index] = '収支';
        matchCount++;
      }
      // カテゴリ列の検出
      else if (this.isCategoryColumn(headerStr)) {
        suggestedMapping[index] = 'カテゴリ';
        matchCount++;
      }
      // メモ・備考列の検出
      else if (this.isMemoColumn(headerStr)) {
        suggestedMapping[index] = 'メモ';
        matchCount++;
      }
      // 摘要列の検出
      else if (this.isSummaryColumn(headerStr)) {
        suggestedMapping[index] = '摘要';
        matchCount++;
      }
      // 種別列の検出
      else if (this.isTypeColumn(headerStr)) {
        suggestedMapping[index] = '収支';
        matchCount++;
      }
      // 収支列の検出
      else if (this.isIncomeExpenseColumn(headerStr)) {
        suggestedMapping[index] = '収支';
        matchCount++;
      }
    });

    // データ行も分析して検出精度を向上
    if (rawData.length > 1) {
      const sampleRows = rawData.slice(1, Math.min(6, rawData.length));
      this.analyzeDataRows(sampleRows, suggestedMapping);
    }

    // 信頼度を計算
    confidence = Math.min(matchCount / Math.max(headerRow.length, 4), 1);

    // パターン名を決定
    const detectedPattern = this.determinePattern(suggestedMapping);

    return {
      suggestedMapping,
      confidence,
      detectedPattern
    };
  }

  /**
   * 日付列かどうかを判定
   */
  private static isDateColumn(header: string): boolean {
    const dateKeywords = ['日付', 'date', '年月日', '取引日', 'いつ', 'when', '日時', 'datetime', '年度', 'fiscal', 'year'];
    if (header === '年') return true; // 明示的にサポート
    return dateKeywords.some(keyword => header.includes(keyword));
  }

  /**
   * 価格/金額列かどうかを判定
   */
  private static isPriceColumn(header: string): boolean {
    const priceKeywords = ['価格', '金額', '値段', 'price', 'amount', '料金', 'いくら', 'how much'];
    return priceKeywords.some(keyword => header.includes(keyword));
  }

  /**
   * 収入列かどうかを判定
   */
  private static isIncomeColumn(header: string): boolean {
    const incomeKeywords = ['収入', 'income', '入金', '受取', 'credit', 'プラス', '+'];
    return incomeKeywords.some(keyword => header.includes(keyword));
  }

  /**
   * 支出列かどうかを判定
   */
  private static isExpenseColumn(header: string): boolean {
    const expenseKeywords = ['支出', 'expense', '出金', '支払', 'debit', 'マイナス', '-'];
    return expenseKeywords.some(keyword => header.includes(keyword));
  }

  /**
   * カテゴリ列かどうかを判定
   */
  private static isCategoryColumn(header: string): boolean {
    const categoryKeywords = ['カテゴリ', 'category', '種類', 'type', '分類', '項目', 'なに', 'what'];
    return categoryKeywords.some(keyword => header.includes(keyword));
  }


  /**
   * メモ・備考列かどうかを判定
   */
  private static isMemoColumn(header: string): boolean {
    const memoKeywords = ['メモ', 'memo', '備考', 'note', '注記', 'remark', 'comment'];
    return memoKeywords.some(keyword => header.includes(keyword));
  }

  /**
   * 摘要列かどうかを判定
   */
  private static isSummaryColumn(header: string): boolean {
    const summaryKeywords = ['摘要', '内容', '明細', 'description', 'subject'];
    return summaryKeywords.some(keyword => header.includes(keyword));
  }

  /**
   * 種別列かどうかを判定
   */
  private static isTypeColumn(header: string): boolean {
    const typeKeywords = ['種別', '区分', 'type', '取引種別'];
    return typeKeywords.some(keyword => header.includes(keyword));
  }

  /**
   * 収支列かどうかを判定
   */
  private static isIncomeExpenseColumn(header: string): boolean {
    const incomeExpenseKeywords = ['収支', '収支区分', '入出金', 'income_expense'];
    return incomeExpenseKeywords.some(keyword => header.includes(keyword));
  }

  /**
   * データ行を分析して列マッピングを調整
   */
  private static analyzeDataRows(sampleRows: any[][], mapping: ColumnMapping): void {
    // 日付らしいデータの検出
    Object.keys(mapping).forEach(colIndexStr => {
      const colIndex = parseInt(colIndexStr);
      const mappingType = mapping[colIndex];
      
      if (mappingType === '日付') {
        // 日付列として正しいかを確認
        const hasDateLikeData = sampleRows.some(row => {
          const cell = row[colIndex];
          return this.looksLikeDateData(cell);
        });
        
        if (!hasDateLikeData) {
          delete mapping[colIndex]; // 日付っぽくないので削除
        }
      }
    });
  }

  /**
   * データが日付らしいかどうかを判定
   */
  private static looksLikeDateData(value: any): boolean {
    if (!value) return false;
    
    const str = String(value).trim();
    
    // 日本語形式の日付
    if (/\d{4}年\d{1,2}月\d{1,2}日/.test(str)) return true;
    
    // スラッシュ区切り
    if (/\d{4}\/\d{1,2}\/\d{1,2}/.test(str)) return true;
    
    // ハイフン区切り
    if (/\d{4}-\d{1,2}-\d{1,2}/.test(str)) return true;

    // 年のみ
    if (/^\d{4}$/.test(str)) return true;
    
    // Excel の日付シリアル値
    if (typeof value === 'number' && value > 40000) return true;
    
    return false;
  }

  /**
   * 検出されたパターンを判定
   */
  private static determinePattern(mapping: ColumnMapping): string {
    const mappingTypes = Object.values(mapping);
    
    // 標準フォーマット（日付、金額、収支、メモ）
    if (mappingTypes.includes('日付') && 
        mappingTypes.includes('金額') && mappingTypes.includes('収支')) {
      return 'standard_format';
    }
    
    if (mappingTypes.includes('日付') && mappingTypes.includes('金額')) {
      return 'standard_ledger';
    }
    
    if (mappingTypes.includes('日付') && mappingTypes.includes('収入') && mappingTypes.includes('支出')) {
      return 'income_expense_split';
    }
    
    return 'unknown';
  }

  /**
   * ファイルからデータを読み取る
   */
  static async parseFile(
    file: File,
    config: FileParserConfig
  ): Promise<FileParseResult> {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    let rawData: any[][] = [];

    try {
      if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        rawData = await this.parseExcel(file);
      } else if (fileExtension === 'csv') {
        rawData = await this.parseCSV(file);
      } else {
        throw new Error('サポートされていないファイル形式です');
      }

      return this.processRawData(rawData, config);
    } catch (error) {
      throw new Error(`ファイル解析エラー: ${error instanceof Error ? error.message : '不明なエラー'}`);
    }
  }

  /**
   * Excel ファイルをパースする
   */
  private static async parseExcel(file: File): Promise<any[][]> {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'buffer' });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
  }

  /**
   * CSV ファイルをパースする
   */
  private static async parseCSV(file: File): Promise<any[][]> {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        complete: (results) => {
          resolve(results.data as any[][]);
        },
        error: (error) => {
          reject(new Error(`CSV解析エラー: ${error.message}`));
        },
        header: false,
        skipEmptyLines: true,
        encoding: 'UTF-8'
      });
    });
  }

  /**
   * 生データを処理して正規化されたトランザクションデータに変換
   */
  private static processRawData(
    rawData: any[][],
    config: FileParserConfig
  ): FileParseResult {
    const hasHeader = config.hasHeader !== false; // デフォルトはtrue
    const dataRows = hasHeader ? rawData.slice(1) : rawData;
    
    const parsedRows: ParsedRowData[] = [];
    const normalizedTransactions: NormalizedTransactionData[] = [];
    const errors: Array<ParsedRowData & { error: string }> = [];

    dataRows.forEach((row, index) => {
      const rowNumber = hasHeader ? index + 2 : index + 1;
      
      try {
        const parsedRow = this.parseRow(row, config.columnMapping, rowNumber, config.fiscalYearStartMonth);
        parsedRows.push(parsedRow);

        if (parsedRow.error) {
          errors.push(parsedRow as ParsedRowData & { error: string });
        } else {
          // 正規化されたトランザクションデータに変換
          const normalized = this.normalizeRowData(parsedRow, config);
          if (normalized) {
            normalizedTransactions.push(normalized);
          }
        }
      } catch (error) {
        const errorRow: ParsedRowData = {
          rowNumber,
          originalData: row,
          error: `行解析エラー: ${error instanceof Error ? error.message : '不明なエラー'}`
        };
        parsedRows.push(errorRow);
        errors.push(errorRow as ParsedRowData & { error: string });
      }
    });

    return {
      parsedRows,
      normalizedTransactions,
      errors,
      totalRows: dataRows.length,
      validRows: normalizedTransactions.length
    };
  }

  /**
   * 行データをパースする
   */
  private static parseRow(
    row: any[],
    columnMapping: ColumnMapping,
    rowNumber: number,
    fiscalYearStartMonth: number = 4
  ): ParsedRowData {
    const parsedRow: ParsedRowData = {
      rowNumber,
      originalData: row
    };

    // 列マッピングに基づいてデータを抽出
    Object.entries(columnMapping).forEach(([colIndexStr, mapping]) => {
      const colIndex = parseInt(colIndexStr);
      const cellValue = row[colIndex] || '';

      switch (mapping) {
        case '日付':
          parsedRow.date = this.parseDate(cellValue, fiscalYearStartMonth);
          break;
        case '金額':
        case '価格':
          const amount = this.parseAmount(cellValue);
          if (amount !== null) {
            parsedRow.amount = Math.abs(amount);
            parsedRow.type = amount >= 0 ? 'income' : 'expense';
          }
          break;
        case '収入':
          const income = this.parseAmount(cellValue);
          if (income !== null && income > 0) {
            parsedRow.amount = income;
            parsedRow.type = 'income';
          }
          break;
        case '支出':
          const expense = this.parseAmount(cellValue);
          if (expense !== null && expense > 0) {
            parsedRow.amount = expense;
            parsedRow.type = 'expense';
          }
          break;
        case '種別':
          const typeStr = String(cellValue).trim().toLowerCase();
          if (typeStr.includes('収入') || typeStr.includes('income')) {
            parsedRow.type = 'income';
          } else if (typeStr.includes('支出') || typeStr.includes('expense')) {
            parsedRow.type = 'expense';
          }
          break;
        case '収支':
          const incomeExpenseStr = String(cellValue).trim().toLowerCase();
          if (incomeExpenseStr.includes('収入') || incomeExpenseStr.includes('income') || incomeExpenseStr === '入') {
            parsedRow.type = 'income';
          } else if (incomeExpenseStr.includes('支出') || incomeExpenseStr.includes('expense') || incomeExpenseStr === '出') {
            parsedRow.type = 'expense';
          }
          break;
        case 'カテゴリ':
          parsedRow.category = String(cellValue).trim();
          break;
        case '備考':
          // 備考は reference_number に格納
          parsedRow.reference_number = String(cellValue).trim();
          break;
        case 'メモ':
          parsedRow.reference_number = String(cellValue).trim();
          break;
        case '摘要':
          parsedRow.description = String(cellValue).trim();
          break;
        case '無視':
          // 何もしない
          break;
      }
    });

    // バリデーション
    const errors: string[] = [];
    if (!parsedRow.date) errors.push('日付が不正または未設定');
    if (!parsedRow.amount || parsedRow.amount <= 0) errors.push('金額が不正または未設定');
    if (!parsedRow.type) errors.push('取引種別が未設定');

    if (errors.length > 0) {
      parsedRow.error = errors.join(', ');
    }

    return parsedRow;
  }

  /**
   * 日付文字列をISO形式に変換
   */
  private static parseDate(value: any, fiscalYearStartMonth: number = 4): string {
    if (!value) return '';

    try {
      // Excel の日付シリアル値の場合
      if (typeof value === 'number' && value > 40000) {
        const date = XLSX.SSF.parse_date_code(value);
        return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
      }

      // 文字列の日付の場合
      const dateStr = String(value).trim();
      
      // 日本語形式の日付パース（例：2024年8月15日）
      const japaneseDate = dateStr.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/);
      if (japaneseDate) {
        const [, year, month, day] = japaneseDate;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      // 年月のみのパターン（例：2024年8月）
      const japaneseYearMonth = dateStr.match(/(\d{4})年(\d{1,2})月/);
      if (japaneseYearMonth) {
        const [, year, month] = japaneseYearMonth;
        return `${year}-${month.padStart(2, '0')}-01`;
      }

      // スラッシュ区切り（例：2024/8/15）
      const slashDate = dateStr.match(/(\d{4})\/(\d{1,2})\/(\d{1,2})/);
      if (slashDate) {
        const [, year, month, day] = slashDate;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      // 年月のみのスラッシュ区切り（例：2024/8）
      const slashYearMonth = dateStr.match(/(\d{4})\/(\d{1,2})/);
      if (slashYearMonth) {
        const [, year, month] = slashYearMonth;
        return `${year}-${month.padStart(2, '0')}-01`;
      }

      // ハイフン区切り（例：2024-8-15）
      const hyphenDate = dateStr.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (hyphenDate) {
        const [, year, month, day] = hyphenDate;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      // 年のみ（例：2024） - 会計年度開始月に設定
      const yearOnly = dateStr.match(/^(\d{4})$/);
      if (yearOnly) {
        const [, year] = yearOnly;
        const month = String(fiscalYearStartMonth).padStart(2, '0');
        return `${year}-${month}-01`;
      }

      // 標準的な Date コンストラクタによるパース
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }

      return '';
    } catch (error) {
      return '';
    }
  }

  /**
   * 金額文字列を数値に変換
   */
  private static parseAmount(value: any): number | null {
    if (!value) return null;

    try {
      // 数値の場合はそのまま返す
      if (typeof value === 'number') {
        return value;
      }

      // 文字列の場合は数値部分を抽出
      const str = String(value).trim();
      
      // カンマを除去
      const cleaned = str.replace(/[,，]/g, '');
      
      // 括弧で囲まれた負の数値の処理
      const parentheses = cleaned.match(/^\((.+)\)$/);
      if (parentheses) {
        const num = parseFloat(parentheses[1]);
        return isNaN(num) ? null : -Math.abs(num);
      }

      // 通常の数値変換
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    } catch (error) {
      return null;
    }
  }

  /**
   * パース済み行データを正規化されたトランザクションデータに変換
   */
  private static normalizeRowData(
    parsedRow: ParsedRowData,
    config: FileParserConfig
  ): NormalizedTransactionData | null {
    if (parsedRow.error || !parsedRow.date || !parsedRow.amount || !parsedRow.type) {
      return null;
    }

    return {
      account_type: config.accountType,
      type: parsedRow.type,
      amount: parsedRow.amount,
      category: parsedRow.category || 'その他',
      transaction_date: parsedRow.date,
      description: parsedRow.description || undefined,
      reference_number: parsedRow.reference_number,
      source: 'file',
      source_metadata: {
        file_name: config.fileName,
        row_number: parsedRow.rowNumber,
        original_data: parsedRow.originalData
      }
    };
  }
}

// プレビュー用のユーティリティ
export class FilePreviewUtil {
  /**
   * ファイルの最初の数行をプレビュー用に読み取る
   */
  static async getPreviewData(file: File, maxRows: number = 10): Promise<any[][]> {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'buffer' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      return (data as any[][]).slice(0, maxRows);
    } else if (fileExtension === 'csv') {
      return new Promise((resolve, reject) => {
        Papa.parse(file, {
          complete: (results) => {
            const data = results.data as any[][];
            resolve(data.slice(0, maxRows));
          },
          error: (error) => {
            reject(error);
          },
          header: false,
          skipEmptyLines: true,
          encoding: 'UTF-8',
          preview: maxRows
        });
      });
    } else {
      throw new Error('サポートされていないファイル形式です');
    }
  }
}
